/**
 * What each downsizing choice does, said where the choice is made.
 *
 * WHY THESE ARE HERE AND NOT ONLY ON THE INTRO PAGE. The module's opening explainer already covers
 * the 24-month exemption and the downsizer contribution in general terms — but that is read once,
 * before any numbers exist, and it cannot say what THIS client's choice does. These say it at the
 * point of decision, with the client's own figures, which is the whole difference between a rule and
 * a consequence.
 *
 * They replace the same content from the floating "What's happening" panel, which a client had to
 * know to open.
 */
import React from 'react';
import { FieldNote } from '../../components/narrative/PlainWords.tsx';
import { formatCurrency } from '../../utils/index.ts';

/**
 * The downsizing trap, named at the moment it becomes true — and pointed at the two things that fix it.
 *
 * WHY THIS EXISTS. The module's opening explainer warns about the trap in the abstract: the home is
 * exempt, the money it becomes is not, so downsizing can cut the Age Pension. Then the client enters
 * their figures, watches the pension fall, and is given a column of allocation boxes with nothing
 * connecting the two. The two boxes that directly soften the trap — a gift within the free area and
 * a funeral bond — sit immediately below and read as just more options (visual pass, 2026-08-28).
 *
 * A reader should not have to work out for themselves which lever addresses the problem the screen
 * has just shown them. This says which, with their own numbers, and only when the pension actually
 * fell — a client whose pension did not move has no trap to fix, and would rightly ignore the rest
 * of the page if told otherwise.
 */
export function DownsizingTrapNote({
    pensionBefore,
    pensionAfter,
    freedCapital,
    alreadySoftened = false,
}: {
    pensionBefore: number;
    pensionAfter: number;
    freedCapital: number;
    /** True once a gift or a funeral bond is already allocated — then the prompt has done its job. */
    alreadySoftened?: boolean;
}) {
    const lost = Math.round(pensionBefore) - Math.round(pensionAfter);
    // Below a token amount there is nothing worth acting on, and a warning would be noise.
    if (lost < 500 || !(freedCapital > 0)) return null;

    const gone = Math.round(pensionAfter) <= 0;

    return (
        <FieldNote
            tone="warn"
            heading={
                gone
                    ? `This is the downsizing trap — the Age Pension stops`
                    : `This is the downsizing trap — ${formatCurrency(lost)} a year of Age Pension`
            }
        >
            <p>
                The home was exempt. The <strong>{formatCurrency(freedCapital)}</strong> it turns into is
                counted, and deemed to earn income — which is what moves the pension
                {gone ? ' all the way to nil' : ''}.
            </p>
            {!alreadySoftened && (
                <p className="mt-1.5">
                    Two of the boxes below put part of that money back outside the assets test: a{' '}
                    <strong>funeral bond</strong> is exempt up to the published limit per person, and a{' '}
                    <strong>gift</strong> within the yearly free area leaves the test straight away. Neither
                    undoes the trap, but both reduce what is counted — and the Suggestion Engine ranks them
                    against the alternatives.
                </p>
            )}
        </FieldNote>
    );
}

/**
 * A gap between selling and buying: the proceeds meant for the new home stay exempt for a while.
 *
 * Only shown when a future date is set, because that is the only time a gap exists to explain. Sold
 * and bought on the same day, there is nothing to say.
 */
export function ProceedsExemptionNote({ downsizeDate, freedCapital }: { downsizeDate: string; freedCapital: number }) {
    if (!downsizeDate) return null;

    return (
        <FieldNote tone="info" heading="If there is a gap before you buy, the money stays exempt for a while">
            <p>
                The part of the proceeds you <strong>intend to use for the new home</strong> is exempt from
                the assets test for up to <strong>24 months</strong> — up to 36 if you are delayed for reasons
                outside your control. During that time it is counted under the income test only, and at the{' '}
                <strong>lower deeming rate</strong>.
            </p>
            <p className="mt-1.5">
                Once you have bought, the new home is exempt and this stops applying. Anything left over —
                {freedCapital > 0 ? <> here about <strong>{formatCurrency(freedCapital)}</strong> —</> : null} is
                counted from the day of sale. Tell Services Australia within <strong>14 days</strong> of selling.
            </p>
        </FieldNote>
    );
}

/**
 * The downsizer contribution: what it buys and what it costs.
 *
 * The trade-off is the point. Moving money from an exempt home into super is tax-effective and
 * makes the money ASSESSABLE once the client is Age Pension age — so a note that mentioned only the
 * tax benefit would be selling half the picture.
 */
export function DownsizerContributionNote({
    clientAmount,
    partnerAmount = 0,
    isPensionAge = true,
}: {
    clientAmount: number;
    partnerAmount?: number;
    isPensionAge?: boolean;
}) {
    const total = (Number(clientAmount) || 0) + (Number(partnerAmount) || 0);
    if (!(total > 0)) return null;

    return (
        <FieldNote tone="info" heading={`${formatCurrency(total)} moves from the sale into super`}>
            <p>
                A downsizer contribution does not count towards your normal contribution caps, and you can make
                it whatever your super balance. Inside super, earnings are taxed at 15% at most, and nothing in
                a retirement pension — usually far less than in your own name.
            </p>
            <p className="mt-1.5">
                {isPensionAge ? (
                    <>
                        <strong>The trade-off:</strong> the home was exempt from the Age Pension assets test and
                        super is not, once you are pension age. So this can lower the pension even while it
                        lowers the tax. Whether that is worth it is what the Suggestion Engine ranks.
                    </>
                ) : (
                    <>
                        <strong>Worth knowing:</strong> super in accumulation is not counted for the Age Pension
                        until you reach pension age. After that it is, so the pension effect arrives later.
                    </>
                )}
            </p>
            <p className="mt-1.5 text-slate-500">
                You can use it <strong>once, ever</strong>, from a single home sale, and it must be made within
                90 days of settlement.
            </p>
        </FieldNote>
    );
}

/**
 * Selling without buying: the change of status that does the damage.
 *
 * Becoming a non-homeowner raises the assets-test threshold and can bring Rent Assistance, but it
 * makes the whole of the home's value assessable — which almost always outweighs both.
 */
export function SellAndRentNote({ salePrice, isCouple = false }: { salePrice: number; isCouple?: boolean }) {
    if (!(salePrice > 0)) return null;

    return (
        <FieldNote tone="warn" heading="Renting makes you a non-homeowner — all of the equity becomes assessable">
            <p>
                Selling without buying turns the whole <strong>{formatCurrency(salePrice)}</strong> into money
                Centrelink counts and deems to earn income. Non-homeowners do get a higher assets-test
                threshold — and Rent Assistance may be payable — but the two rarely make up for counting the
                lot.
            </p>
            <p className="mt-1.5">
                Rent also becomes a lifelong cost, and there is no longer a home growing in the background for
                the estate. It suits people who want the flexibility and no maintenance; it usually reduces the
                pension more than buying somewhere cheaper does.
                {isCouple ? ' Both of you are assessed as non-homeowners.' : ''}
            </p>
        </FieldNote>
    );
}
